import { useCallback, useEffect, useMemo, useRef } from 'react';
import { type InfiniteData, keepPreviousData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

import type { ILinks } from '../../gonfalon';
import { queryKeys } from '../../lib/queryKeys';
import { observableToPromise } from '../../shared/observableToPromise';
import { type AuditLogEntryResponse, getAccountAuditLogEntries } from '../api';
import type { Account, AuditLogEntries, AuditLogEntry } from '../model';

import { DEFAULT_ACCOUNT_QUERY_OPTIONS } from './useAccountQuery';

// Returned from getPreviousPageParam so the "Load newer" control can appear.
// The infinite query never fetches this page; newer entries are loaded beside
// it and inserted together when the user asks.
const NEWER_PAGE_MARKER = 'newer';

// Page param for rows inserted by "Load newer". A refetch must return these
// rows again. Using the initial page param (`undefined`) would re-request the
// anchored window and wipe the inserted rows.
const REVEALED_NEWER_PAGE = 'revealed-newer';

// Page param for the anchored window, the page the list starts from. A refetch
// replays the pages in order, asking `getNextPageParam` what follows each one,
// and stops as soon as that answer is null. The window is the page after the
// revealed rows, so it needs a param that can be named and is not null, which
// is what it would be if the initial param stayed `undefined`.
const WINDOW_PAGE = 'window';

// A batch aims for this many rows. Deliberately below the API's limit of 20 so
// a slightly wrong estimate under-fills, which still returns usable rows,
// rather than over-fills, which wastes the request entirely.
const TARGET_BATCH_SIZE = 12;
// How many times a range is narrowed or stretched before giving up.
const MAX_RANGE_ATTEMPTS = 6;
const MIN_RANGE_MS = 1;
const MAX_RANGE_MS = 7 * 24 * 60 * 60 * 1000;
const DEFAULT_RANGE_MS = 60_000;

type NewerBatch = {
  items: AuditLogEntry[];
  reachedHead: boolean;
  nextRangeMs: number;
};

function clampRangeMs(ms: number) {
  return Math.min(MAX_RANGE_MS, Math.max(MIN_RANGE_MS, ms));
}

// Seeds the first range from how densely packed the rows already on screen are.
function estimateRangeMs(loaded: AuditLogEntry[]) {
  if (loaded.length < 2) {
    return DEFAULT_RANGE_MS;
  }
  const span = loaded[0].date - loaded[loaded.length - 1].date;
  if (span <= 0) {
    return MIN_RANGE_MS;
  }
  return clampRangeMs(Math.round((span / loaded.length) * TARGET_BATCH_SIZE));
}

/**
 * Fetches the entries immediately newer than `fromDate`.
 *
 * The API pages only toward older entries, so there is no cursor for "the next
 * 20 newer". Bounding a range with both `after` and `before` gets there
 * instead: when a range holds no more than one page, the response is every
 * entry in it, and its oldest rows are the ones adjoining `fromDate`. A `next`
 * link means the range held more than a page, so the rows returned are its
 * newest end rather than the ones wanted, and the range has to shrink.
 *
 * A range starts at `fromDate` itself so that entries sharing that timestamp
 * are not skipped, which means the row already at the top of the list comes
 * back every time. `isKnown` is what separates a range that genuinely holds
 * something new from one that only echoed what the caller already has.
 */
async function fetchNewerBatch(
  account: Account,
  params: {
    query?: string;
    spec?: string;
    member?: string;
    fuzzy?: boolean;
    actions?: readonly string[];
  },
  fromDate: number,
  isKnown: (entry: AuditLogEntry) => boolean,
  rangeHintMs: number,
  signal: AbortSignal,
): Promise<NewerBatch> {
  // An omitted `beforeCursor` means the range runs to whenever the server
  // thinks now is. That is the only honest way to ask for "everything left":
  // a browser clock running behind the server's would put the newest entries
  // above any bound taken from it, and the range would come back empty and be
  // read as having caught up.
  const fetchRange = (afterCursor: number, beforeCursor?: number) =>
    observableToPromise(
      getAccountAuditLogEntries({
        account,
        query: params.query,
        spec: params.spec,
        member: params.member,
        fuzzy: params.fuzzy,
        actions: params.actions,
        afterCursor,
        beforeCursor,
      }),
      signal,
    );

  const rangeMs = clampRangeMs(rangeHintMs);
  // Bounds on where the upper end of a usable range lies. `emptyUpTo` is the
  // highest end already shown to hold nothing new, `overfullFrom` the lowest
  // shown to hold more than a page. Each attempt moves one of them, so the two
  // close on each other. Sizing the next attempt from `fromDate` instead would
  // keep re-probing the stretch already known to be empty, and a quiet spell
  // below a burst of activity would stall.
  let emptyUpTo = fromDate;
  let overfullFrom: number | undefined;
  // Set once a range comes back holding nothing new, to stretch the next one
  // all the way to the present. Expressed as intent rather than a width
  // because `now` moves between attempts and a width would land just short.
  let stretchToPresent = false;

  for (let attempt = 0; attempt < MAX_RANGE_ATTEMPTS; attempt++) {
    const now = Date.now();
    let upper: number;
    if (overfullFrom !== undefined) {
      upper = emptyUpTo + Math.floor((overfullFrom - emptyUpTo) / 2);
      if (upper <= emptyUpTo) {
        // The bounds have met: more than a page shares a single millisecond.
        // No range is narrow enough, so leave it to the caller to offer a jump
        // to the newest.
        break;
      }
    } else {
      upper = stretchToPresent ? now : Math.min(fromDate + rangeMs, now);
    }
    const reachesPresent = upper >= now;
    // Both cursors are exclusive, so widen by a millisecond at each end. That
    // keeps entries sharing `fromDate`, which the caller drops by id.
    const response: AuditLogEntryResponse = await fetchRange(fromDate - 1, reachesPresent ? undefined : upper + 1);

    if (response._links?.next?.href) {
      overfullFrom = upper;
      continue;
    }

    const fresh = response.items.filter((item) => !isKnown(item));

    if (fresh.length === 0) {
      if (reachesPresent) {
        return { items: [], reachedHead: true, nextRangeMs: rangeMs };
      }
      emptyUpTo = upper;
      // Nothing new anywhere in the range, so whatever is left sits above it.
      // Going straight to the present settles it in one more request: either
      // the remainder fits in a page and all of it comes back, or the `next`
      // link says there is more than a page and bisection takes over.
      // Creeping upward instead would take a click per step, which is the
      // behaviour this replaces.
      stretchToPresent = true;
      continue;
    }

    const probedMs = Math.max(MIN_RANGE_MS, upper - fromDate);

    return {
      items: fresh,
      reachedHead: reachesPresent,
      // Tuned from every row in the range, not just the new ones, since it is
      // the density of the log that decides how wide the next range should be.
      nextRangeMs: clampRangeMs(Math.round((probedMs * TARGET_BATCH_SIZE) / response.items.length)),
    };
  }

  return { items: [], reachedHead: false, nextRangeMs: rangeMs };
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError';
}

export function useAuditLogEntries(
  account: Account | null | undefined,
  params: {
    query?: string;
    spec?: string;
    member?: string;
    fuzzy?: boolean;
    date?: ReadonlyArray<Date | null>;
    actions?: readonly string[];
    beforeCursor?: number;
  } = {},
) {
  const { query, spec, member, fuzzy, date, actions, beforeCursor } = params;
  const searchParams = useMemo(
    () => ({ query, spec, member, fuzzy, date, actions, beforeCursor }),
    [query, spec, member, fuzzy, date, actions, beforeCursor],
  );

  const queryClient = useQueryClient();
  const accountId = account?._id;
  const queryKey = useMemo(
    () =>
      accountId
        ? queryKeys.accounts.auditLogEntries(accountId, searchParams)
        : ['accounts', 'audit-entries', null, searchParams],
    [accountId, searchParams],
  );
  const reachedHeadRef = useRef(params.beforeCursor === undefined);
  const revealedItemsRef = useRef<AuditLogEntry[] | null>(null);
  // Carried between clicks so the range converges on the log's actual density
  // instead of re-guessing from scratch every time.
  const rangeHintRef = useRef<number | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);
  const newerGenerationRef = useRef(0);

  // Rows already inserted by "Load newer", read back from the cache. The ref is
  // per-mount, but the cached page outlives a remount, so this is what stops a
  // refetch from replacing those rows with an empty page.
  const readRevealedFromCache = useCallback(() => {
    const cached = queryClient.getQueryData<InfiniteData<AuditLogEntryResponse, string | undefined>>(queryKey);
    if (cached?.pageParams[0] !== REVEALED_NEWER_PAGE) {
      return null;
    }
    return cached.pages[0]?.items ?? null;
  }, [queryClient, queryKey]);

  // Everything on screen, newest first. The first row is where the next batch
  // has to continue from, whether it came from the window or an earlier click.
  const readLoadedFromCache = useCallback(() => {
    const cached = queryClient.getQueryData<InfiniteData<AuditLogEntryResponse, string | undefined>>(queryKey);
    return cached?.pages.flatMap((page) => page.items) ?? [];
  }, [queryClient, queryKey]);

  useEffect(() => {
    newerGenerationRef.current += 1;
    reachedHeadRef.current = params.beforeCursor === undefined;
    revealedItemsRef.current = readRevealedFromCache();
    rangeHintRef.current = undefined;
    abortRef.current?.abort();
    abortRef.current = null;
  }, [params.beforeCursor, readRevealedFromCache]);

  const infiniteQuery = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) => {
      if (pageParam === REVEALED_NEWER_PAGE) {
        const revealed = revealedItemsRef.current ?? readRevealedFromCache() ?? [];
        return Promise.resolve({ items: revealed, _links: {} as ILinks });
      }
      if (!account || pageParam === NEWER_PAGE_MARKER) {
        return Promise.resolve({ items: [], _links: {} as ILinks });
      }
      return observableToPromise(
        getAccountAuditLogEntries({
          account,
          nextUrl: pageParam === WINDOW_PAGE ? undefined : pageParam,
          ...searchParams,
        }),
        signal,
      );
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      // The revealed rows carry no `next` link of their own. Saying so here
      // would end a refetch at that page and take the window, the linked entry
      // and every older page down with it.
      if (lastPageParam === REVEALED_NEWER_PAGE) {
        return WINDOW_PAGE;
      }
      return lastPage?._links?.next?.href;
    },
    getPreviousPageParam: (firstPage) => {
      if (reachedHeadRef.current || firstPage.items?.[0]?.date === undefined) {
        return undefined;
      }
      return NEWER_PAGE_MARKER;
    },
    initialPageParam: WINDOW_PAGE as string | undefined,
    placeholderData: keepPreviousData,
    enabled: Boolean(account),
    ...DEFAULT_ACCOUNT_QUERY_OPTIONS,
  });

  useEffect(() => () => abortRef.current?.abort(), []);

  // Manual prefetching of next page for instant Load More
  useEffect(() => {
    if (infiniteQuery.data?.pages && account && !infiniteQuery.isFetching) {
      const lastPage = infiniteQuery.data.pages[infiniteQuery.data.pages.length - 1];
      const nextUrl = lastPage?._links?.next?.href;

      if (nextUrl) {
        // Prefetch the next page in background for faster Load More
        queryClient.prefetchQuery({
          queryKey: [...queryKeys.accounts.auditLogEntries(account._id, searchParams), 'page', nextUrl],
          queryFn: ({ signal }) =>
            observableToPromise(
              getAccountAuditLogEntries({
                account,
                nextUrl,
                ...searchParams,
              }),
              signal,
            ),
          staleTime: DEFAULT_ACCOUNT_QUERY_OPTIONS.staleTime * 6, // Longer stale time for prefetched data
          gcTime: DEFAULT_ACCOUNT_QUERY_OPTIONS.gcTime,
        });
      }
    }
  }, [infiniteQuery.data?.pages, account, infiniteQuery.isFetching, queryClient, searchParams]);

  // Marks the list as caught up. `reachedHead` lives in a ref, so the query
  // needs a new data object before it will re-read it and drop the control.
  const markReachedHead = useCallback(() => {
    reachedHeadRef.current = true;
    queryClient.setQueryData<InfiniteData<AuditLogEntryResponse, string | undefined>>(queryKey, (old) =>
      old ? { pages: [...old.pages], pageParams: [...old.pageParams] } : old,
    );
  }, [queryClient, queryKey]);

  const revealNewer = useCallback(async () => {
    const generation = newerGenerationRef.current;
    if (!account) {
      return 'empty' as const;
    }
    const loaded = readLoadedFromCache();
    const fromDate = loaded[0]?.date;
    if (fromDate === undefined) {
      return 'empty' as const;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const alreadyLoaded = new Set(loaded.map((item) => item._id));

    let batch: NewerBatch;
    try {
      batch = await fetchNewerBatch(
        account,
        searchParams,
        fromDate,
        (item) => alreadyLoaded.has(item._id),
        rangeHintRef.current ?? estimateRangeMs(loaded),
        controller.signal,
      );
    } catch (error: unknown) {
      if (isAbortError(error) || controller.signal.aborted) {
        return 'empty' as const;
      }
      throw error;
    }

    if (generation !== newerGenerationRef.current) {
      return 'empty' as const;
    }
    rangeHintRef.current = batch.nextRangeMs;

    if (!batch.items.length) {
      if (batch.reachedHead) {
        markReachedHead();
        return 'complete' as const;
      }
      return 'empty' as const;
    }

    // Each batch sits directly on top of the previous one, so it goes on the
    // front. Appending would put the newest rows below the older ones.
    const merged = [...batch.items, ...(revealedItemsRef.current ?? readRevealedFromCache() ?? [])];
    revealedItemsRef.current = merged;
    if (batch.reachedHead) {
      reachedHeadRef.current = true;
    }

    await queryClient.cancelQueries({ queryKey });
    if (generation !== newerGenerationRef.current) {
      return 'empty' as const;
    }
    const revealedPage = { items: merged, _links: {} as ILinks };
    queryClient.setQueryData<InfiniteData<AuditLogEntryResponse, string | undefined>>(queryKey, (old) => {
      if (!old) {
        return old;
      }
      const alreadyRevealed = old.pageParams[0] === REVEALED_NEWER_PAGE;
      return {
        pages: alreadyRevealed ? [revealedPage, ...old.pages.slice(1)] : [revealedPage, ...old.pages],
        pageParams: alreadyRevealed ? old.pageParams : [REVEALED_NEWER_PAGE, ...old.pageParams],
      };
    });
    return batch.reachedHead ? ('complete' as const) : ('partial' as const);
  }, [account, queryClient, queryKey, searchParams, readLoadedFromCache, readRevealedFromCache, markReachedHead]);

  // Transform the infinite query data to match the existing AuditLogEntries interface
  const data: AuditLogEntries = useMemo(() => {
    const allEntries = infiniteQuery.data?.pages.flatMap((page) => page.items) ?? [];
    const lastPage = infiniteQuery.data?.pages[infiniteQuery.data.pages.length - 1];
    const links = lastPage?._links ?? ({} as ILinks);
    const canLoadNewer = infiniteQuery.hasPreviousPage;

    return {
      entries: allEntries,
      items: allEntries,
      _links: links,
      isFetching: infiniteQuery.isFetching,
      isPlaceholderData: infiniteQuery.isPlaceholderData,
      loadMore: infiniteQuery.hasNextPage ? infiniteQuery.fetchNextPage : undefined,
      loadNewer: canLoadNewer ? revealNewer : undefined,
    };
  }, [
    infiniteQuery.data,
    infiniteQuery.isFetching,
    infiniteQuery.isPlaceholderData,
    infiniteQuery.hasNextPage,
    infiniteQuery.hasPreviousPage,
    infiniteQuery.fetchNextPage,
    revealNewer,
  ]);

  return {
    ...infiniteQuery,
    data,
  };
}
